/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import apiFetch from '@/services/apiFetch.ts';
import { getParcelByPin } from '@/services/parcels.endpoint.ts';
import { geocodeByAddress } from 'components/fragment/SearchInputLocation/SearchInputLocation.tsx';
// *: Imported from its own module rather than the `components/primitive` barrel, which
// *: would pull every re-exported component into a hook that needs one of them. The
// *: barrel is safe under vitest now that `vite.config.ts` aliases
// *: `@zawarski/palmetto-ui-components` to its prebuilt ESM bundle in the `test` block;
// *: keeping this import deep is about the dependency, not the test runner.
import ToastMessage from 'components/primitive/ToastMessage';
import { ToastStatus } from '@/common/enum';
import { Nullable } from 'interfaces/Nullable.ts';

/*
  Parcel ID Autofill Flow:

    parcel_id changes on the form (typed in ModalNoParcel, or edited later)
         │
         ▼
    skip when empty, or when it still matches the value this hook last looked up
    (that baseline tracks the saved record, so hydrating an existing entry never
     re-fetches over already-saved data -- even when the record arrives after mount)
         │
         ├── abort any previous in-flight lookup (AbortController)
         │
         ▼
    apiFetch(getParcelByPin(pin), controller)   + setTimeout -> controller.abort()
         │                                        (cleared as soon as the fetch settles)
         ├── 404 / PIN mismatch / 0 results ──▶ toast "not found", leave fields as-is
         │
         ├── unusable record shape ──▶ toast "lookup failed", nothing written
         │   (only the fields this caller applies are checked)
         │
         ├── superseded (a newer pin took over) ──▶ silent, no toast
         │
         ├── failure (network / 5xx / timeout) ──▶ toast error, baseline cleared so
         │                                          the same pin can be retried
         │
         └── success ──▶ Owner        → ownerFields.ownerName    ┐ written only when
                          MarketValue  → ownerFields.marketValue  ┘ the caller maps them
                          SiteAddress  → address
                          MailCityState → city / state / zipCode (parsed)
                          │
                          └──▶ geocodeByAddress("SiteAddress, County County, SC")
                                  + its own timeout
                                  ├── success ──▶ latitude / longitude
                                  └── failure / empty / no Google script ──▶ leave lat/lon blank
*/

export const PARCEL_LOOKUP_TIMEOUT_MS = 15000;
// *: The geocoder settles only from Google's callback and takes no abort signal, so it
// *: needs a bound of its own -- the lookup's AbortController cannot reach it.
export const GEOCODE_TIMEOUT_MS = 10000;

// *: Exported so tests can assert which message a path produces, not just that some
// *: toast fired -- the two used to be indistinguishable to the suite.
export const NOT_FOUND_MESSAGE =
  'That Parcel ID was not found. You can still enter the details manually.';
export const LOOKUP_FAILED_MESSAGE =
  'Could not look up that Parcel ID. You can still enter the details manually.';

const HTTP_NOT_FOUND = 404;

// *: Every county this product serves is in South Carolina, and the parcels table is
// *: keyed the same way, so the parcel's state is a constant rather than a field on the
// *: record. It anchors the geocode query -- county names repeat across states (there is
// *: a Greenwood County in Kansas too), so county alone is not enough to disambiguate.
export const PARCEL_STATE = 'SC';

// *: Google's Geocoder result, narrowed to the two accessors this hook reads.
type GeocodeResult = {
  geometry?: { location?: { lat: () => number; lng: () => number } };
};

type ParcelLookupResponse = { data?: Nullable<unknown[]> };

/**
 * The form fields that receive the parcel's owner name and market value, for a caller
 * whose form carries them. A form with no equivalent field passes `null` instead.
 */
export type ParcelOwnerFields = {
  ownerName: string;
  marketValue: string;
};

type NormalizeParcelOptions = {
  /**
   * Whether the caller applies `Owner` and `MarketValue`. False skips those two checks,
   * so a record stays usable for a caller that never reads either field.
   */
  validateOwnerFields?: boolean;
};

/** A parcel record whose every field has been checked before anything is written. */
export type NormalizedParcel = {
  PIN: string;
  Owner: string;
  MarketValue: Nullable<number>;
  MailCityState: string;
  SiteAddress: string;
  County: string;
};

/**
 * Coerces a wire value to text. Absent values become '', scalars become their string
 * form, and anything else (array, object, boolean) returns null to mark the record
 * unusable rather than stringifying into garbage.
 */
function toText(value: unknown): string | null {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

/**
 * Coerces a wire value to currency. Returns `null` when absent and `undefined` when
 * present but unusable -- the caller treats `undefined` as a malformed record. Booleans
 * are rejected rather than coerced, so `true` cannot become a replacement cost of 1.
 */
function toCurrency(value: unknown): number | null | undefined {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * Validates the whole record up front so a bad field can never leave the form
 * half-overwritten. Returns null when the record cannot be trusted, which the caller
 * reports as a failed lookup without writing anything.
 *
 * `validateOwnerFields` narrows the check to the fields the caller will actually apply:
 * a caller that never writes Owner or MarketValue should not lose a whole lookup over a
 * value it discards. It defaults to on, so a single-argument call behaves as it always
 * has. Narrowing the check does not narrow the returned shape -- both keys still come
 * back, an unusable value normalized to '' or null rather than left undefined.
 */
export function normalizeParcel(
  record: unknown,
  { validateOwnerFields = true }: NormalizeParcelOptions = {}
): NormalizedParcel | null {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
  const raw = record as Record<string, unknown>;

  const PIN = toText(raw.PIN);
  const Owner = toText(raw.Owner);
  const MailCityState = toText(raw.MailCityState);
  const SiteAddress = toText(raw.SiteAddress);
  const County = toText(raw.County);
  const MarketValue = toCurrency(raw.MarketValue);

  if (PIN === null || MailCityState === null || SiteAddress === null || County === null) {
    return null;
  }

  if (validateOwnerFields && (Owner === null || MarketValue === undefined)) return null;

  // *: Owner and MarketValue keep their declared types even when their checks were
  // *: skipped, so a caller that does not map them still gets a well-formed record.
  return {
    PIN,
    Owner: Owner ?? '',
    MarketValue: MarketValue ?? null,
    MailCityState,
    SiteAddress,
    County,
  };
}

/**
 * Splits a `MailCityState` value ("CHARLESTON, SC 29401") into its parts.
 * Returns null when the value is missing or carries no comma, which is the
 * signal to skip City/State/Zip autofill without touching the other fields.
 */
export function parseMailCityState(
  mailCityState: Nullable<string>
): { city: string; state: string; zipCode: string } | null {
  if (!mailCityState) return null;

  const commaIndex = mailCityState.indexOf(',');
  if (commaIndex === -1) return null;

  const city = mailCityState.slice(0, commaIndex).trim();
  const remainder = mailCityState
    .slice(commaIndex + 1)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!city && !remainder.length) return null;

  return {
    city,
    state: remainder[0] ?? '',
    zipCode: remainder[1] ?? '',
  };
}

/**
 * Builds the address string handed to the geocoder.
 *
 * `SiteAddress` arrives street-only ("107 TURKEY CREEK RD"), which geocodes ambiguously --
 * the same street name exists in dozens of states, and the geocoder is given no region
 * bias. The parcel's own `County` is authoritative for where the parcel physically sits,
 * so it anchors the query alongside {@link PARCEL_STATE}.
 *
 * `MailCityState` is deliberately left out even though its city and zip are already
 * parsed: that is the *owner's mailing* address, not the site's. For an absentee owner it
 * names a different town -- often a different state -- than the parcel, which would pull
 * the pin somewhere worse than the unanchored street would.
 *
 * Returns '' when there is no street to geocode, which the caller reads as "skip".
 */
export function buildGeocodeQuery(siteAddress: string, county: string): string {
  const street = siteAddress.trim();
  if (!street) return '';

  const countyName = county.trim();
  if (!countyName) return `${street}, ${PARCEL_STATE}`;

  // *: Records carry a bare name ("Greenwood"), but tolerate one that already spells out
  // *: the suffix so the query cannot read "Greenwood County County".
  const qualified = /\bcounty$/i.test(countyName) ? countyName : `${countyName} County`;
  return `${street}, ${qualified}, ${PARCEL_STATE}`;
}

type UseParcelAutofillArgs = {
  form: UseFormReturn<any>;
  /**
   * The Parcel ID already persisted on the record being opened (null for a new
   * entry). Used as the baseline so the value hydrated by `form.reset()` does not
   * trigger a lookup over already-saved fields. Containers that load the record
   * asynchronously may supply this after mount; the baseline re-seeds when it does.
   */
  initialParcelID?: Nullable<string>;
  /**
   * The form fields that receive the parcel's `Owner` and `MarketValue`, or `null` for a
   * form that carries neither. Required rather than optional on purpose: a call site
   * that forgets it should fail `tsc`, not quietly stop filling the two fields.
   */
  ownerFields: Nullable<ParcelOwnerFields>;
  timeoutMs?: number;
  geocodeTimeoutMs?: number;
};

type UseParcelAutofillResult = {
  /** True while a lookup is in flight, so callers can disable Save and show progress. */
  isLookingUp: boolean;
};

/**
 * Watches `parcel_id` and autofills the caller's form from the matching parcel record.
 *
 * Shadow paths at this entry point:
 *   - happy: parcel found, shape valid, PIN matches -> fields applied below
 *   - nil/null: `parcel_id` null or non-string -> normalized to '' and skipped
 *   - empty/zero: empty or whitespace-only pin, or zero results -> skipped / not-found toast
 *   - upstream error: network, 5xx, 404, timeout, unusable shape -> caught below,
 *     toast, no field written
 */
const useParcelAutofill = ({
  form,
  ownerFields,
  initialParcelID = null,
  timeoutMs = PARCEL_LOOKUP_TIMEOUT_MS,
  geocodeTimeoutMs = GEOCODE_TIMEOUT_MS,
}: UseParcelAutofillArgs): UseParcelAutofillResult => {
  const rawParcelID = form.watch('parcel_id');
  // *: Derive the trimmed pin during render and key the effect on it, so a
  // *: whitespace-only edit cannot tear down an in-flight lookup without starting one.
  const pin = typeof rawParcelID === 'string' ? rawParcelID.trim() : '';
  const savedPin = initialParcelID?.trim() ?? '';
  // *: Read out as two strings rather than kept as the object: call sites inline
  // *: `ownerFields`, so the object's identity changes on every render and depending on
  // *: it would restart the lookup effect continuously. The names themselves are stable.
  const ownerNameField = ownerFields?.ownerName ?? null;
  const marketValueField = ownerFields?.marketValue ?? null;

  const lastLookupPinRef = useRef<string>(savedPin);
  const seededPinRef = useRef<string>(savedPin);
  const [isLookingUp, setIsLookingUp] = useState(false);

  // *: Declared before the lookup effect so it runs first on the same commit.
  // *: The map viewer hydrates its record from the store after mount, so
  // *: `initialParcelID` can arrive on a later render than the one that mounts this
  // *: hook -- without re-seeding, that container's `form.reset()` would read as a
  // *: user edit and re-fetch over saved data.
  useEffect(() => {
    if (savedPin && savedPin !== seededPinRef.current) {
      seededPinRef.current = savedPin;
      lastLookupPinRef.current = savedPin;
    }
  }, [savedPin]);

  useEffect(() => {
    if (!pin || pin === lastLookupPinRef.current) return;
    lastLookupPinRef.current = pin;

    // *: The option supplies both names or neither, so one flag covers the pair. It
    // *: gates the two writes below and the matching checks in `normalizeParcel`.
    const appliesOwnerFields = ownerNameField !== null && marketValueField !== null;

    const controller = new AbortController();
    // *: `cancelled` covers everything this effect run may still write (including the
    // *: geocode, which has no abort signal); `timedOut` separates a timeout abort
    // *: (a real failure the user should see) from a superseded abort (silent).
    let cancelled = false;
    let timedOut = false;
    const timeoutID = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const applyGeocode = async (query: string) => {
      if (!query) return;
      let geocodeTimeoutID: ReturnType<typeof setTimeout> | undefined;
      try {
        const results = (await Promise.race([
          geocodeByAddress(query),
          new Promise<never>((_resolve, reject) => {
            geocodeTimeoutID = setTimeout(
              () => reject(new Error(`geocoding exceeded ${geocodeTimeoutMs}ms`)),
              geocodeTimeoutMs
            );
          }),
        ])) as GeocodeResult[] | undefined;
        if (cancelled) return;
        const location = results?.[0]?.geometry?.location;
        if (!location) return;
        form.setValue('latitude', location.lat().toFixed(8));
        form.setValue('longitude', location.lng().toFixed(8));
      } catch (error) {
        // *: Lat/Lon stay blank; every other field already landed.
        console.error(`🚀 useParcelAutofill geocoding failed for parcel ${pin}:`, error);
      } finally {
        clearTimeout(geocodeTimeoutID);
      }
    };

    const notFound = () => {
      toast.error(<ToastMessage status={ToastStatus.ERROR} message={NOT_FOUND_MESSAGE} />);
    };

    const runLookup = async () => {
      setIsLookingUp(true);
      try {
        const response = await apiFetch<ParcelLookupResponse>(getParcelByPin(pin), controller);
        // *: The fetch has settled, so the lookup budget is spent -- release it before
        // *: the geocode so the two legs do not share one window.
        clearTimeout(timeoutID);
        if (cancelled) return;

        console.log(
          `🚀 useParcelAutofill response for parcel ${pin}:`,
          JSON.stringify(response.data)
        );

        const record = response.data?.data?.[0];
        if (record === undefined || record === null) {
          console.warn(`🚀 useParcelAutofill no parcel record returned for ${pin}`);
          notFound();
          return;
        }

        // *: Validate the whole record before writing anything. A bad field used to
        // *: throw partway through application, leaving the form half-overwritten and
        // *: then error-toasted.
        const parcel = normalizeParcel(record, { validateOwnerFields: appliesOwnerFields });
        if (!parcel) {
          console.error(
            `🚀 useParcelAutofill unusable parcel record for ${pin}:`,
            JSON.stringify(record)
          );
          toast.error(<ToastMessage status={ToastStatus.ERROR} message={LOOKUP_FAILED_MESSAGE} />);
          return;
        }

        // *: Treat a record whose PIN does not match what was asked for as not-found
        // *: rather than trusting the response blindly.
        if (parcel.PIN.trim() !== pin) {
          console.warn(
            `🚀 useParcelAutofill PIN mismatch -- requested "${pin}", backend returned "${parcel.PIN}". ` +
              `Nothing was autofilled. The backend route ignores the ?pin= parameter, so it ` +
              `answers every lookup with the same arbitrary row.`
          );
          notFound();
          return;
        }

        // *: Written unconditionally: a new lookup replaces the previous parcel's
        // *: values even when the new record leaves a field empty, so one parcel's
        // *: owner can never be saved against another parcel's ID. The owner pair is
        // *: skipped entirely for a form that has no field for it, but once mapped it
        // *: follows the same overwrite rule as the rest.
        if (appliesOwnerFields) {
          form.setValue(ownerNameField, parcel.Owner);
          form.setValue(marketValueField, parcel.MarketValue);
        }
        form.setValue('address', parcel.SiteAddress);

        // *: Written only when present, unlike the fields above. `county` is required
        // *: by the form schema and a new entry pre-fills it from the user's group, so
        // *: clearing it on a record that carries no County would invalidate the form
        // *: and throw away a usable default. When the parcel does name a county it
        // *: wins, so a parcel from a neighbouring county is not filed under the
        // *: assessor's own.
        if (parcel.County.trim()) form.setValue('county', parcel.County.trim());

        // *: City/State/Zip stay on the documented skip rule -- an unparseable
        // *: MailCityState leaves them alone rather than clearing them.
        const cityStateZip = parseMailCityState(parcel.MailCityState);
        if (cityStateZip) {
          form.setValue('city', cityStateZip.city);
          form.setValue('state', cityStateZip.state);
          form.setValue('zipCode', cityStateZip.zipCode);
        }

        await applyGeocode(buildGeocodeQuery(parcel.SiteAddress, parcel.County));
      } catch (error) {
        // *: A newer Parcel ID superseded this lookup (or the form unmounted) --
        // *: that is a cancellation, not a failure, so it stays silent.
        if (cancelled && !timedOut) return;
        console.error(`🚀 useParcelAutofill lookup failed for parcel ${pin}:`, error);

        // *: apiFetch collapses every non-2xx into a generic Error, so the status it
        // *: attaches is the only way to tell a wrong Parcel ID from a broken backend.
        if (!timedOut && (error as { status?: number } | null)?.status === HTTP_NOT_FOUND) {
          notFound();
          return;
        }

        // *: A transient failure has to stay retryable: clear the baseline so returning
        // *: to this Parcel ID runs a fresh lookup instead of hitting the skip guard.
        lastLookupPinRef.current = '';
        toast.error(<ToastMessage status={ToastStatus.ERROR} message={LOOKUP_FAILED_MESSAGE} />);
      } finally {
        clearTimeout(timeoutID);
        // *: A superseded run must not clear the flag out from under its replacement.
        if (!cancelled) setIsLookingUp(false);
      }
    };

    void runLookup();

    return () => {
      cancelled = true;
      clearTimeout(timeoutID);
      controller.abort();
    };
  }, [pin, form, timeoutMs, geocodeTimeoutMs, ownerNameField, marketValueField]);

  return { isLookingUp };
};

export default useParcelAutofill;
